import React from 'react';
import { Zap, Shield, Headphones, TrendingUp, Clock, Award } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const iconMap = {
  zap: Zap,
  shield: Shield,
  headphones: Headphones,
  'trending-up': TrendingUp,
  clock: Clock,
  award: Award
};

const AdvantagesSection = () => {
  const { t } = useLanguage();

  const advantages = [
    {
      id: 1,
      title: t('advantages.items.performance.title'),
      description: t('advantages.items.performance.description'),
      icon: "zap"
    },
    {
      id: 2,
      title: t('advantages.items.security.title'),
      description: t('advantages.items.security.description'),
      icon: "shield"
    },
    {
      id: 3,
      title: t('advantages.items.support.title'),
      description: t('advantages.items.support.description'),
      icon: "headphones"
    },
    {
      id: 5,
      title: t('advantages.items.delivery.title'),
      description: t('advantages.items.delivery.description'),
      icon: "clock"
    },
    {
      id: 6,
      title: t('advantages.items.expertise.title'),
      description: t('advantages.items.expertise.description'),
      icon: "award"
    }
  ];

  return (
    <section className="py-10 md:py-20 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <AnimateOnScroll animation="fade-down" className="text-center mb-8 md:mb-16">
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-4 md:mb-6">
            {t('advantages.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4 px-2">
            {t('advantages.title')} <span className="text-cyan-500">{t('advantages.titleHighlight')}</span> ?
          </h2>
          <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto px-4">
            {t('advantages.subtitle')}
          </p>
        </AnimateOnScroll>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {advantages.map((advantage, index) => {
            const Icon = iconMap[advantage.icon];
            return (
              <AnimateOnScroll 
                key={advantage.id}
                animation="fade-up"
                delay={index * 80}
              >
                <div 
                  className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl md:rounded-2xl p-4 md:p-8 hover:border-cyan-600/50 transition-all duration-300 group card-hover h-full"
                >
                  {/* Icon */}
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-cyan-950/50 border border-cyan-800/50 flex items-center justify-center mb-3 md:mb-6 group-hover:bg-cyan-600 transition-all duration-300">
                    <Icon className="w-5 h-5 md:w-7 md:h-7 text-cyan-500 group-hover:text-white transition-colors" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-base md:text-xl font-bold text-white mb-1.5 md:mb-3">{advantage.title}</h3>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">{advantage.description}</p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;
